// Journal of user changes (events) which have to be synced with the backend.
//
// state = {
// 	subscriptions: [
// 		{
// 			id: 321,
// 			floorplan: "2+kk",
// 			metadata: { status: "Synced" }
// 		},
// 		{
// 			id: 0,
// 			floorplan: "5+1",
// 			metadata: { status: "Pending", key: "..." }
// 		}
// 	],
// 	log: [
// 		{
// 			type: "Add",
// 			key: "...",		// unique event key, ties event to the view
// 			id: 0,		// subscription id, 0 for not yet created
// 			data: { id: 0, floorplan: "5+1" }
// 		},
// 		{
// 			type: "Remove",
// 			key: "...",
// 			id: 322
// 		}
// 	]
// }
//
// The view (state.subscriptions) is always kept in sync with the journal:
// log_item() applies the event effect to the view right away (optimistic
// update). Events stay in state.log until the backend acknowledges them.
// Only on ack (ack_backend_state()) the view is replaced by the backend
// state and acknowledged events are dropped from the journal.
//
// All changes of subscriptions and journal are stored in localStorage.

const STATE_KEY = "state";

export const ItemType = {
  Add: 'Add',
  Remove: 'Remove',
  Edit: 'Edit',
};

export const SubscriptionStatus = {
  Pending: 'Pending',
  Synced: 'Synced',
  Error: 'Error',
};

export function new_key() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random()}`;
}

// Keep the view ordered oldest → newest. Pending subscriptions have no
// created_at yet (the backend assigns it on the first sync), so they sort
// last — a newly created subscription stays at the end of the list. Equal
// created_at (e.g. subs created in one sync batch share the transaction
// timestamp) falls back to id, which reflects the insertion order. Pending
// subs tie on both (created_at missing, id 0), the stable sort keeps their
// creation order.
export function sortSubscriptions(subscriptions) {
  subscriptions.sort((a, b) => {
    const at = a.created_at ?? Infinity;
    const bt = b.created_at ?? Infinity;
    if (at !== bt) return at - bt;
    return a.id - b.id;
  });
}

export function save_state(state) {
  localStorage.setItem(
    STATE_KEY,
    JSON.stringify({ subscriptions: state.subscriptions, log: state.log })
  );
}

export function load_state() {
  var raw = localStorage.getItem(STATE_KEY);
  if (raw === null) {
    return null;
  }

  var state;
  try {
    state = JSON.parse(raw);
  } catch (error) {
    console.log(`Corrupted state in local storage: ${error}`);
    return null;
  }

  if (
    state === null ||
    typeof state !== "object" ||
    !Array.isArray(state.subscriptions) ||
    !Array.isArray(state.log)
  ) {
    console.log("Invalid state in local storage.");
    return null;
  }

  return state;
}

// Apply the effect of a single event on the view (state.subscriptions).
// Non-destructive: the event itself is not touched. Idempotent for Add
// events (matched by key), so flush can be safely repeated.
function apply_effect(state, item) {
  switch (item.type) {
    case ItemType.Add: {
      if (!item.key) {
        item.key = new_key();
      }
      const exists = state.subscriptions.some(
        (s) => s.metadata && s.metadata.key === item.key
      );
      if (exists) {
        break;
      }
      // JSON round-trip instead of structuredClone: the item can be a
      // reactive $state proxy which structuredClone can not clone
      var subscription = JSON.parse(JSON.stringify(item.data));
      subscription.metadata = {
        status: SubscriptionStatus.Pending,
        key: item.key,
      };
      state.subscriptions.push(subscription);
      break;
    }
    case ItemType.Remove:
      state.subscriptions = state.subscriptions.filter(
        (s) => s.id !== item.id
      );
      break;
    case ItemType.Edit: {
      const sub = state.subscriptions.find((s) => s.id == item.id);
      if (!sub) {
        console.log(`No subscription ${item.id} for edit.`);
        break;
      }
      for (const [key, value] of Object.entries(item.data)) {
        sub[key] = value;
      }
      break;
    }
    default:
      console.log(`Unknown item type ${item.type}.`);
  }
}

// Apply the event to the view, store event in the journal and persist the
// state. The event is kept in the journal until the backend acknowledges it.
export function log_item(state, item) {
  if (!item.key) {
    item.key = new_key();
  }
  state.log.push(item);
  apply_effect(state, item);
  sortSubscriptions(state.subscriptions);
  save_state(state);
}

// Apply all pending events on the view without consuming the journal.
export function log_flush(state) {
  for (const item of state.log) {
    apply_effect(state, item);
  }
}

// Drop the journal event (destructive) and apply its effect. Mostly for
// tests, the sync flow uses log_flush() instead.
export function log_apply_item(state, i) {
  if (state.log.length <= i)
    return;
  const item = state.log.splice(i, 1)[0];

  apply_effect(state, item);
}

// Cancel a not yet synced Add event. The subscription is removed from the
// view and the Add event is dropped from the journal. Nothing has to be
// synced with the backend.
export function cancel_add(state, sub) {
  const key = sub && sub.metadata && sub.metadata.key;
  if (!key) {
    console.log("Can not cancel Add without event key.");
    return false;
  }

  const index = state.log.findIndex(
    (item) => item.type === ItemType.Add && item.key === key
  );
  if (index === -1) {
    console.log(`No pending Add event ${key} in the journal.`);
    return false;
  }

  state.log.splice(index, 1);
  state.subscriptions = state.subscriptions.filter(
    (s) => !(s.metadata && s.metadata.key === key)
  );
  save_state(state);

  return true;
}

// Drop pending Add events which the backend would reject. is_valid decides
// whether an event's location can still be applied (predefined location id
// known to this frontend, "any location" or a drawn region). Used to cancel
// events created by an older frontend which reference location ids that do
// not exist anymore (locations were re-keyed to RÚIAN ids) — syncing them
// would fail with an FK violation and leave the event pending forever.
// Returns true when at least one event was dropped.
/**
 * @param {{ subscriptions: any[], log: any[] }} state
 * @param {(id: number) => boolean} is_valid
 * @returns {boolean}
 */
export function drop_stale_adds(state, is_valid) {
  const stale = (state.log || []).filter(
    (item) =>
      item.type === ItemType.Add &&
      item.data &&
      item.data.locations &&
      !is_valid(item.data.locations.predefined)
  );
  if (stale.length === 0) {
    return false;
  }

  const keys = new Set(stale.map((item) => item.key).filter(Boolean));
  const stale_set = new Set(stale);

  state.log = state.log.filter((item) => !stale_set.has(item));
  state.subscriptions = state.subscriptions.filter(
    (s) => !(s.metadata && s.metadata.key && keys.has(s.metadata.key))
  );
  save_state(state);

  return true;
}

// Drop all pending Add events. Used when the backend rejects a sync with
// 413 — the user is at the maximum number of subscriptions, so the Adds can
// not be applied until something is removed. Remove events are kept: they
// only lower the count.
/**
 * @param {{ subscriptions: any[], log: any[] }} state
 * @returns {boolean}
 */
export function drop_pending_adds(state) {
  const keys = new Set(
    (state.log || [])
      .filter((item) => item.type === ItemType.Add)
      .map((item) => item.key)
      .filter(Boolean)
  );

  state.log = state.log.filter((item) => item.type !== ItemType.Add);
  state.subscriptions = state.subscriptions.filter(
    (s) => !(s.metadata && s.metadata.key && keys.has(s.metadata.key))
  );
  save_state(state);

  return keys.size > 0;
}

// Backend acknowledged the sent events (and maybe some new ones arrived
// meanwhile). Replace the view with the backend state, keep events which
// were not part of the ack and re-apply them on the new view.
export function ack_backend_state(state, json, sent) {
  const sent_keys = new Set(sent.map((item) => item.key));

  state.log = state.log.filter((item) => !sent_keys.has(item.key));
  state.subscriptions = json.subscriptions.map((sub) => ({
    ...sub,
    metadata: { status: SubscriptionStatus.Synced },
  }));
  log_flush(state);
  sortSubscriptions(state.subscriptions);
  save_state(state);

  return state;
}

// Replace the whole state with the backend one. All pending events are
// considered acknowledged.
export function merge_backend_state(state, json) {
  return ack_backend_state(state, json, state.log);
}

// State with all pending events applied on top of the current view.
export function get_state(state) {
  var clone = structuredClone(state);

  log_flush(clone);

  return clone;
}
